// ─────────────────────────────────────────────────────────────
// HarmanaGEL — Mobil uygulama sohbet (WhatsApp benzeri) verisine
// admin panelinden salt-okunur erişim.
//
// Mobil uygulamadaki kullanıcı-kullanıcı mesajlaşması Firebase
// Firestore'da tutulur (SQL değil). Yapı:
//
//   Message (collection)                         → her doc bir sohbet
//     ├─ advertID:        string  (ILANLAR.ID)
//     ├─ participantIds:  [string]               → sohbetteki kullanıcı ID'leri
//     ├─ participants:    { [uid]: {name,photo,phone} }
//     ├─ lastMessageText, lastMessageAt, lastSenderId
//     └─ customerMessage (subcollection)         → mesajlar
//          ├─ customerMessage: string (metin / görsel URL)
//          ├─ type:            'text' | 'image' | 'location'
//          ├─ senderId:        string
//          ├─ messageDate:     'YYYY/MM/DD H:mm:ss'
//          └─ lat, lng (type === 'location' için)
//
// Not: Admin tüm sohbetleri okuyabilmelidir. Firestore güvenlik
// kurallarınız yalnızca sohbet katılımcılarına okuma izni veriyorsa,
// admin panelinin okuyabilmesi için kurallara admin erişimi
// eklenmelidir (ör. auth token custom claim veya panel için ayrı
// okuma izni). Kurallar engellerse aşağıdaki çağrılar hata döner ve
// arayüz kullanıcıya bilgi mesajı gösterir.
// ─────────────────────────────────────────────────────────────

import { initializeApp, getApp, getApps } from "firebase/app";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

// Mobil uygulama ile AYNI Firebase projesi (harmanagelmobileapp).
// Değerler google-services.json içindeki public config'ten alınmıştır.
const chatFirebaseConfig = {
  apiKey: "AIzaSyCsNvXt_LI0osvz1WvU8kxiwcvEaSr_kgM",
  authDomain: "harmanagelmobileapp.firebaseapp.com",
  projectId: "harmanagelmobileapp",
  storageBucket: "harmanagelmobileapp.firebasestorage.app",
  messagingSenderId: "183958527677",
  appId: "1:183958527677:android:a64fc0f4cc579505fea1d4",
};

// Auth için kullanılan olası varsayılan firebase app'i ile çakışmamak
// adına ayrı isimli bir app instance'ı kullanıyoruz.
const CHAT_APP_NAME = "harmanagel-chat";

const getChatApp = () => {
  const existing = getApps().find((a) => a.name === CHAT_APP_NAME);
  if (existing) return existing;
  return initializeApp(chatFirebaseConfig, CHAT_APP_NAME);
};

const getChatDb = () => getFirestore(getChatApp());

const getChatStorage = () => getStorage(getChatApp());

/**
 * Bir görsel dosyasını Firebase Storage'a yükler ve indirilebilir URL döner.
 * Mobil uygulama ile AYNI proje/bucket kullanıldığından yüklenen dosyalar
 * hem panelde hem uygulamada görünür. Mobil uygulamadaki path düzeniyle
 * uyumludur (ör. "magaza_logolari/...", "magaza_kapaklari/...").
 *
 * @param {File} file           Yüklenecek dosya (input type=file)
 * @param {string} path         Storage içindeki hedef yol (uzantı dahil)
 * @param {(pct:number)=>void} [onProgress] Yüzde (0-100) ilerleme callback'i
 * @returns {Promise<string>}   getDownloadURL sonucu (kalıcı URL)
 */
export const uploadImageToStorage = (file, path, onProgress) =>
  new Promise((resolve, reject) => {
    try {
      const fileRef = storageRef(getChatStorage(), path);
      const task = uploadBytesResumable(fileRef, file, {
        contentType: file.type || "image/jpeg",
      });
      task.on(
        "state_changed",
        (snap) => {
          if (onProgress && snap.totalBytes) {
            onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
          }
        },
        (err) => reject(err),
        () => {
          getDownloadURL(task.snapshot.ref).then(resolve).catch(reject);
        }
      );
    } catch (err) {
      reject(err);
    }
  });

/**
 * Bir ilana ait tüm sohbetleri getirir (advertID eşleşen).
 * @param {number|string} advertId ILANLAR.ID
 * @returns {Promise<Array>} sohbet dokümanları
 */
export const getConversationsByAdvert = async (advertId) => {
  const db = getChatDb();
  const q = query(
    collection(db, "Message"),
    where("advertID", "==", String(advertId))
  );
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  // En son mesajlaşan sohbet üstte (lastMessageAt string 'YYYY/MM/DD H:mm:ss').
  rows.sort((a, b) =>
    String(b.lastMessageAt || "").localeCompare(String(a.lastMessageAt || ""))
  );
  return rows;
};

/**
 * Uygulamadaki TÜM sohbetleri getirir (admin genel mesajlaşma ekranı için).
 * En son mesajlaşan üstte olacak şekilde sıralanır. Firestore orderBy'ı,
 * lastMessageAt alanı olmayan dokümanları hariç tutabildiğinden sıralama
 * istemci tarafında yapılır (159 civarı doküman; sorun olmaz).
 * @returns {Promise<Array>} sohbet dokümanları
 */
export const getAllConversations = async () => {
  const db = getChatDb();
  const snap = await getDocs(collection(db, "Message"));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) =>
    String(b.lastMessageAt || "").localeCompare(String(a.lastMessageAt || ""))
  );
  return rows;
};

/**
 * Bir sohbete ait mesajları tarih artan sırada getirir.
 * @param {string} conversationId Message doküman ID'si
 * @returns {Promise<Array>} mesaj dokümanları
 */
export const getConversationMessages = async (conversationId) => {
  const db = getChatDb();
  const q = query(
    collection(db, "Message", conversationId, "customerMessage"),
    orderBy("messageDate", "asc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export { getChatApp, getChatDb };
